import { createEntityAdapter, createSlice, EntityState, PayloadAction } from "@reduxjs/toolkit";
import { compareNum } from "../../app/arrays";
import { RootState, store } from "../../app/store";
import { logout } from "../user/userSlice";

// expiration time in ms
const expiration: number = 30 * 60 * 1000 // 30 min

export type CacheResult
    = { tag: 'nothing' }
    | { tag: 'expired' }
    | { tag: 'ok', data: object }

export function tryGetCacheEntry(id: string | number, type: CacheEntryType): CacheResult {
    const entry = selectCacheEntryById(store.getState(), buildKey(id, type))

    if (entry === undefined) {
        return { tag: 'nothing' }
    } else {
        const now = Date.now()
        const { timestamp, data } = entry
        if (now - timestamp < expiration) {
            return {
                tag: 'ok',
                data,
            }
        } else {
            return {
                tag: 'expired',
            }
        }
    }
}

function buildKey(id: string | number, type: CacheEntryType): string {
    return `${type}|${id}`
}

export type CacheEntryType
    = 'urs'
    | 'ra'

export interface CacheEntry {
    key: string
    timestamp: number
    data: object
}

export const adapter = createEntityAdapter<CacheEntry>({
    selectId: (entry) => entry.key,
    sortComparer: (a, b) => compareNum(a.timestamp, b.timestamp),
})

export interface CacheState {
    items: EntityState<CacheEntry>
}

const initialState: CacheState = {
    items: adapter.getInitialState(),
}

export const cacheSlice = createSlice({
    name: "cache",
    initialState,
    reducers: {
        clearCache: (state) => {
            adapter.removeAll(state.items)
        },
        removeCacheEntry: (state, action: PayloadAction<string>) => {
            adapter.removeOne(state.items, action.payload)
        },
        addCacheEntry: {
            reducer: (state, action: PayloadAction<CacheEntry>) => {
                adapter.setOne(state.items, action.payload)
            },
            prepare: (arg: { id: string | number, data: object, type: CacheEntryType }) => {
                const { id, data, type } = arg
                const payload: CacheEntry = {
                    key: buildKey(id, type),
                    data,
                    timestamp: Date.now(),
                }
                return {
                    payload,
                }
            },
        },
    },
    extraReducers: (builder) => {
        builder.addCase(logout.fulfilled, (state) => {
            adapter.removeAll(state.items)
        })
    },
})

const {
    selectById: selectCacheEntryById
} = adapter.getSelectors<RootState>((state) => state.cache.items)

export const { addCacheEntry, removeCacheEntry } = cacheSlice.actions

export default cacheSlice.reducer
